import type {
	CalendarDayShift,
	CalendarWorkOrder,
} from "@/shared/repositories/WorkOrderRepository";
import type { CalendarEvent } from "@/shared/repositories/CalendarEventRepository";
import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CalendarItem } from "../components/EventBar";
import { isPlainLeft } from "../utils/calendarUtils";

const moment = getMomentTz();

const DRAG_THRESHOLD = 5;
// Cursor row band (px) used to find snap candidates when not directly over a bar
const ROW_BAND = 40;
// How close to a bar's edge the cursor has to be for that edge to win over the
// free hour under the cursor
const SNAP_PX = 14;

// right button inert while left is down: no menu, no drop
const blockContextMenu = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

export interface DropLineInfo {
	x: number;
	y: number;
	height: number;
	/** empty = free hour slot (blue); snap = another bar's edge (cyan) */
	position: "empty" | "snap";
	dateKey: string;
	targetHour?: number;
	targetKey?: string;
	/** snap: anchor the dragged start to the target's Start instead of its End */
	useStart?: boolean;
}

export interface PreviewDates {
	start: Date;
	end: Date;
}

export interface MoveRequest {
	item: CalendarItem;
	newStart: Date;
	newEnd: Date;
	/** resolved for display; the SP re-resolves from newStart when null */
	targetShift: CalendarDayShift | null;
	insertAfterWorkOrderId: number | null;
	queue: CalendarWorkOrder[];
	insertIndex: number;
	/** planned work orders in the way — the cascade will push them later */
	pushCount: number;
	blocked: boolean;
	/** timeline neighbors at the new position (planned work orders) */
	neighborBefore: CalendarItem | null;
	neighborAfter: CalendarItem | null;
}

type Params = {
	items: CalendarItem[];
	workOrdersList: CalendarWorkOrder[];
	dayShifts: CalendarDayShift[];
	/** changes when the grid lays out other days (month flip mid-drag) */
	layoutKey: string;
	/** the grid still shows the previous month's plan: nothing can land yet */
	stale: boolean;
	onOpenWorkOrder: (wo: CalendarWorkOrder) => void;
	onOpenEvent: (event: CalendarEvent) => void;
	onMoveRequest: (req: MoveRequest) => void;
	onDragEnd?: () => void;
};

const sameLine = (a: DropLineInfo | null, b: DropLineInfo | null): boolean => {
	if (a === null || b === null) return a === b;
	return (
		a.position === b.position &&
		a.dateKey === b.dateKey &&
		a.targetHour === b.targetHour &&
		a.targetKey === b.targetKey &&
		a.useStart === b.useStart
	);
};

export const useCalendarDrag = ({
	items,
	workOrdersList,
	dayShifts,
	layoutKey,
	stale,
	onOpenWorkOrder,
	onOpenEvent,
	onMoveRequest,
	onDragEnd,
}: Params) => {
	const [drag, setDrag] = useState<{ item: CalendarItem } | null>(null);
	const [dropLine, setDropLine] = useState<DropLineInfo | null>(null);
	const [preview, setPreview] = useState<PreviewDates | null>(null);
	const [freeMode, setFreeMode] = useState(false);

	const draggedRef = useRef<CalendarItem | null>(null);
	const dropLineRef = useRef<DropLineInfo | null>(null);
	const previewRef = useRef<PreviewDates | null>(null);
	const freeModeRef = useRef(false);
	// The ghost follows the mouse via direct style writes (no re-render per pixel)
	const ghostRef = useRef<HTMLDivElement | null>(null);
	const posRef = useRef({ x: 0, y: 0 });
	const rafRef = useRef<number | null>(null);
	const listenersRef = useRef<{
		move: (e: MouseEvent) => void;
		up: (e: MouseEvent) => void;
		key: (e: KeyboardEvent) => void;
		remeasure: () => void;
	} | null>(null);
	// Held between mousedown and the drag threshold; cleared on unmount too
	const pendingRef = useRef<{
		move: (e: MouseEvent) => void;
		up: (e: MouseEvent) => void;
	} | null>(null);

	// Geometry is measured once per drag (and after scroll/resize) instead of
	// per frame: reading rects inside the rAF forces a reflow on every move.
	const rectsRef = useRef<{
		bars: { key: string; rect: DOMRect }[];
		days: Map<string, DOMRect>;
	} | null>(null);

	const measure = useCallback(() => {
		const bars: { key: string; rect: DOMRect }[] = [];
		for (const el of document.querySelectorAll<HTMLElement>(
			"[data-item-key]",
		)) {
			const key = el.dataset.itemKey;
			if (key) bars.push({ key, rect: el.getBoundingClientRect() });
		}
		const days = new Map<string, DOMRect>();
		for (const el of document.querySelectorAll<HTMLElement>("[data-date]")) {
			const key = el.dataset.date;
			if (key) days.set(key, el.getBoundingClientRect());
		}
		rectsRef.current = { bars, days };
	}, []);

	const dataRef = useRef({ items, workOrdersList, dayShifts, stale });
	useEffect(() => {
		dataRef.current = { items, workOrdersList, dayShifts, stale };
	}, [items, workOrdersList, dayShifts, stale]);
	const callbacksRef = useRef({
		onOpenWorkOrder,
		onOpenEvent,
		onMoveRequest,
		onDragEnd,
	});
	useEffect(() => {
		callbacksRef.current = {
			onOpenWorkOrder,
			onOpenEvent,
			onMoveRequest,
			onDragEnd,
		};
	}, [onOpenWorkOrder, onOpenEvent, onMoveRequest, onDragEnd]);

	const positionGhost = useCallback(() => {
		const ghost = ghostRef.current;
		if (ghost) {
			ghost.style.left = `${posRef.current.x + 12}px`;
			ghost.style.top = `${posRef.current.y + 16}px`;
		}
	}, []);

	const cleanup = useCallback(() => {
		if (listenersRef.current) {
			document.removeEventListener("mousemove", listenersRef.current.move);
			document.removeEventListener("mouseup", listenersRef.current.up);
			document.removeEventListener("keydown", listenersRef.current.key);
			window.removeEventListener(
				"scroll",
				listenersRef.current.remeasure,
				true,
			);
			window.removeEventListener("resize", listenersRef.current.remeasure);
			listenersRef.current = null;
		}
		document.removeEventListener("contextmenu", blockContextMenu, true);
		if (pendingRef.current) {
			document.removeEventListener("mousemove", pendingRef.current.move);
			document.removeEventListener("mouseup", pendingRef.current.up);
			pendingRef.current = null;
		}
		if (rafRef.current != null) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		rectsRef.current = null;
		draggedRef.current = null;
		dropLineRef.current = null;
		previewRef.current = null;
		freeModeRef.current = false;
		setDrag(null);
		setDropLine(null);
		setPreview(null);
		setFreeMode(false);
	}, []);

	const computePreview = useCallback(
		(line: DropLineInfo): PreviewDates | null => {
			const dragged = draggedRef.current;
			if (!dragged) return null;
			const duration = dragged.end.getTime() - dragged.start.getTime();
			const serverTz = TimeService.getInstance().getServerTimeZone();

			let newStart: Date | null = null;
			if (line.position === "snap" && line.targetKey) {
				const target = dataRef.current.items.find(
					(it) => it.key === line.targetKey,
				);
				if (target) newStart = line.useStart ? target.start : target.end;
			} else if (line.position === "empty" && line.targetHour !== undefined) {
				newStart = moment
					.tz(line.dateKey, "YYYY-MM-DD", serverTz)
					.hour(line.targetHour)
					.minute(0)
					.second(0)
					.millisecond(0)
					.toDate();
			}

			if (!newStart) return null;
			return { start: newStart, end: new Date(newStart.getTime() + duration) };
		},
		[],
	);

	const applyDropLine = useCallback(
		(line: DropLineInfo | null) => {
			if (sameLine(dropLineRef.current, line)) return;
			dropLineRef.current = line;
			setDropLine(line);
			const p = line ? computePreview(line) : null;
			previewRef.current = p;
			setPreview(p);
		},
		[computePreview],
	);

	const resolveDrop = useCallback(
		(x: number, y: number, free: boolean) => {
			const dragged = draggedRef.current;
			if (!dragged) return;

			const rects = rectsRef.current;
			if (!rects) return;

			// Blocked/pushed/neighbours are read off the loaded plan: a drop on
			// last month's data would confirm against the wrong work orders
			if (dataRef.current.stale) {
				applyDropLine(null);
				return;
			}

			let dateKey: string | null = null;
			let dayRect: DOMRect | null = null;
			for (const [key, rect] of rects.days) {
				if (
					x >= rect.left &&
					x <= rect.right &&
					y >= rect.top &&
					y <= rect.bottom
				) {
					dateKey = key;
					dayRect = rect;
					break;
				}
			}
			if (!dateKey || !dayRect) {
				applyDropLine(null);
				return;
			}

			const emptyLine = (): DropLineInfo => {
				const pct = Math.max(
					0,
					Math.min(1, (x - dayRect.left) / dayRect.width),
				);
				const hour = Math.min(23, Math.max(0, Math.round(pct * 24)));
				return {
					x: dayRect.left + (hour / 24) * dayRect.width,
					y: dayRect.top,
					height: dayRect.height,
					position: "empty",
					dateKey,
					targetHour: hour,
				};
			};

			// Non-work-order events (breaks, maintenance…) may legitimately land on
			// top of a work order — snapping to bar edges would make that impossible
			if (free || dragged.kind !== "wo") {
				applyDropLine(emptyLine());
				return;
			}

			// Anchoring to a target's Start drops the bar on top of it and relies on
			// the plan cascade pushing it forward, which only pending work orders do
			const canAnchorToStart = (key: string) => {
				const target = dataRef.current.items.find((it) => it.key === key);
				return (
					target?.kind === "wo" &&
					target.wo.Status === "Pending" &&
					target.event.EventId != null
				);
			};

			// Directly over another bar: first half anchors to its Start, second to its End
			// (last match wins — later siblings paint on top)
			let hovered: { key: string; rect: DOMRect } | null = null;
			for (const bar of rects.bars) {
				if (bar.key === dragged.key) continue;
				const { rect } = bar;
				if (
					x >= rect.left &&
					x <= rect.right &&
					y >= rect.top &&
					y <= rect.bottom
				)
					hovered = bar;
			}
			if (hovered) {
				const { rect } = hovered;
				const firstHalf =
					x < rect.left + rect.width / 2 && canAnchorToStart(hovered.key);
				applyDropLine({
					x: firstHalf ? rect.left : rect.right,
					y: dayRect.top,
					height: dayRect.height,
					position: "snap",
					dateKey,
					targetKey: hovered.key,
					useStart: firstHalf,
				});
				return;
			}

			// Not over a bar: snap to the closest edge among bars in the cursor's row
			// band, but only right beside it — past SNAP_PX the space is a free hour
			// again, otherwise a back-to-back plan pins every bar to its own edge.
			let best: { x: number; key: string; useStart: boolean } | null = null;
			let bestDist = Number.POSITIVE_INFINITY;
			for (const bar of rects.bars) {
				if (bar.key === dragged.key) continue;
				const { rect } = bar;
				if (y < rect.top - ROW_BAND || y > rect.bottom + ROW_BAND) continue;
				if (rect.right < dayRect.left || rect.left > dayRect.right) continue;
				// The left edge lets a bar land ahead of the first one of the day
				const edges = canAnchorToStart(bar.key)
					? ([
							[rect.left, true],
							[rect.right, false],
						] as const)
					: ([[rect.right, false]] as const);
				for (const [edge, useStart] of edges) {
					const dist = Math.abs(x - edge);
					if (dist < bestDist) {
						bestDist = dist;
						best = { x: edge, key: bar.key, useStart };
					}
				}
			}

			if (best && bestDist <= SNAP_PX) {
				applyDropLine({
					x: best.x,
					y: dayRect.top,
					height: dayRect.height,
					position: "snap",
					dateKey,
					targetKey: best.key,
					useStart: best.useStart,
				});
			} else {
				applyDropLine(emptyLine());
			}
		},
		[applyDropLine],
	);

	const buildMoveRequest = useCallback((): MoveRequest | null => {
		const item = draggedRef.current;
		const p = previewRef.current;
		if (!item || !p) return null;

		const newStart = p.start;
		const newEnd = p.end;
		if (
			newStart.getTime() === item.start.getTime() &&
			newEnd.getTime() === item.end.getTime()
		)
			return null;

		if (item.kind !== "wo") {
			const checksWorkOrders = ["M", "D", "H"].includes(
				item.event.EventTypeCode,
			);
			const overlappingWos = checksWorkOrders
				? (dataRef.current.items.filter(
						(it) =>
							it.kind === "wo" &&
							it.wo.Status !== "Cancelled" &&
							it.start < newEnd &&
							it.end > newStart,
					) as Extract<CalendarItem, { kind: "wo" }>[])
				: [];
			return {
				item,
				newStart,
				newEnd,
				targetShift: null,
				insertAfterWorkOrderId: null,
				queue: [],
				insertIndex: 0,
				pushCount:
					item.event.EventTypeCode === "H"
						? 0
						: overlappingWos.filter(
								(it) => it.wo.Status === "Pending" && it.event.EventId != null,
							).length,
				blocked: overlappingWos.some(
					(it) => it.wo.Status !== "Pending" || it.event.EventId == null,
				),
				neighborBefore: null,
				neighborAfter: null,
			};
		}

		const { dayShifts, workOrdersList, items } = dataRef.current;
		// All displayed work orders occupy their window, including legacy shifts.
		const overlapping = items.filter(
			(it) =>
				it.kind === "wo" &&
				it.wo.Status !== "Cancelled" &&
				it.wo.WorkOrderId !== item.wo.WorkOrderId &&
				it.start < newEnd &&
				it.end > newStart,
		) as Extract<CalendarItem, { kind: "wo" }>[];
		const pushCount = overlapping.filter(
			(it) => it.wo.Status === "Pending" && it.event.EventId != null,
		).length;
		// Running/loaded/completed work orders own their window, and so do
		// maintenance/downtime/holiday periods: landing on them is refused
		const blocked =
			overlapping.some(
				(it) => it.wo.Status !== "Pending" || it.event.EventId == null,
			) ||
			items.some(
				(it) =>
					it.kind === "other" &&
					["M", "D", "H"].includes(it.event.EventTypeCode) &&
					it.start < newEnd &&
					it.end > newStart,
			);

		// Timeline neighbors at the new position: the plan we land after, and
		// the first one at-or-past our new start (it follows us, pushed or not)
		const planned = items
			.filter(
				(it): it is Extract<CalendarItem, { kind: "wo" }> =>
					it.kind === "wo" &&
					it.event.EventId != null &&
					it.wo.WorkOrderId !== item.wo.WorkOrderId,
			)
			.sort((a, b) => a.start.getTime() - b.start.getTime());
		const neighborBefore =
			[...planned]
				.reverse()
				.find((it) => it.end.getTime() <= newStart.getTime()) ?? null;
		const neighborAfter =
			planned.find((it) => it.end.getTime() > newStart.getTime()) ?? null;
		const t = newStart.getTime();
		const targetShift =
			dayShifts.find(
				(s) =>
					t >= moment.utc(s.Start).valueOf() && t < moment.utc(s.End).valueOf(),
			) ?? null;

		const startByWo = new Map<number, number>();
		for (const it of items) {
			if (it.kind === "wo")
				startByWo.set(it.wo.WorkOrderId, it.start.getTime());
		}

		const queue = targetShift
			? workOrdersList
					.filter(
						(w) =>
							w.DisplayShiftId === targetShift.ShiftId &&
							w.WorkOrderId !== item.wo.WorkOrderId,
					)
					.sort(
						(a, b) =>
							(startByWo.get(a.WorkOrderId) ?? 0) -
								(startByWo.get(b.WorkOrderId) ?? 0) || a.Sequence - b.Sequence,
					)
			: [];

		let insertIndex = 0;
		for (const w of queue) {
			if ((startByWo.get(w.WorkOrderId) ?? 0) <= t) insertIndex++;
			else break;
		}

		return {
			item,
			newStart,
			newEnd,
			targetShift,
			insertAfterWorkOrderId: targetShift
				? insertIndex === 0
					? 0
					: queue[insertIndex - 1].WorkOrderId
				: null,
			queue,
			insertIndex,
			pushCount,
			blocked,
			neighborBefore,
			neighborAfter,
		};
	}, []);

	const activateDrag = useCallback(
		(item: CalendarItem, x: number, y: number) => {
			draggedRef.current = item;
			posRef.current = { x, y };
			setDrag({ item });
			measure();
			resolveDrop(x, y, false);
			// The ghost mounts on the next render; position it before paint
			requestAnimationFrame(positionGhost);

			const move = (e: MouseEvent) => {
				posRef.current = { x: e.clientX, y: e.clientY };
				if (freeModeRef.current !== e.shiftKey) {
					freeModeRef.current = e.shiftKey;
					setFreeMode(e.shiftKey);
				}
				if (rafRef.current == null) {
					rafRef.current = requestAnimationFrame(() => {
						rafRef.current = null;
						positionGhost();
						resolveDrop(
							posRef.current.x,
							posRef.current.y,
							freeModeRef.current,
						);
					});
				}
			};
			const up = (e: MouseEvent) => {
				if (e.button !== 0) return;
				const req = buildMoveRequest();
				callbacksRef.current.onDragEnd?.();
				cleanup();
				if (req) callbacksRef.current.onMoveRequest(req);
			};
			const key = (e: KeyboardEvent) => {
				if (e.key === "Escape") cleanup();
			};
			// Cached rects are viewport-relative: a wheel scroll invalidates them
			const remeasure = () => {
				measure();
				resolveDrop(posRef.current.x, posRef.current.y, freeModeRef.current);
			};

			listenersRef.current = { move, up, key, remeasure };
			document.addEventListener("mousemove", move);
			document.addEventListener("mouseup", up);
			document.addEventListener("keydown", key);
			window.addEventListener("scroll", remeasure, true);
			window.addEventListener("resize", remeasure);
		},
		[resolveDrop, buildMoveRequest, cleanup, positionGhost, measure],
	);

	const handleItemMouseDown = useCallback(
		(e: React.MouseEvent, item: CalendarItem) => {
			if (!isPlainLeft(e)) return;
			e.preventDefault();
			e.stopPropagation();
			document.addEventListener("contextmenu", blockContextMenu, true);

			const startX = e.clientX;
			const startY = e.clientY;
			let activated = false;

			const detach = () => {
				document.removeEventListener("mousemove", pendingMove);
				document.removeEventListener("mouseup", pendingUp);
				pendingRef.current = null;
			};
			const pendingMove = (me: MouseEvent) => {
				if (activated) return;
				const dist = Math.hypot(me.clientX - startX, me.clientY - startY);
				if (dist > DRAG_THRESHOLD) {
					activated = true;
					detach();
					activateDrag(item, me.clientX, me.clientY);
				}
			};
			const pendingUp = (me: MouseEvent) => {
				if (me.button !== 0) return;
				detach();
				document.removeEventListener("contextmenu", blockContextMenu, true);
				if (!activated) {
					if (item.kind === "wo") callbacksRef.current.onOpenWorkOrder(item.wo);
					else callbacksRef.current.onOpenEvent(item.event);
				}
			};

			pendingRef.current = { move: pendingMove, up: pendingUp };
			document.addEventListener("mousemove", pendingMove);
			document.addEventListener("mouseup", pendingUp);
		},
		[activateDrag],
	);

	// A month flip mid-drag re-lays the grid, and its plan arrives a moment
	// later: both invalidate the cached rects and the current drop target
	// biome-ignore lint/correctness/useExhaustiveDependencies: items/layoutKey/stale are the triggers
	useEffect(() => {
		if (!draggedRef.current) return;
		const id = requestAnimationFrame(() => {
			if (!draggedRef.current) return;
			measure();
			resolveDrop(posRef.current.x, posRef.current.y, freeModeRef.current);
		});
		return () => cancelAnimationFrame(id);
	}, [items, layoutKey, stale, measure, resolveDrop]);

	useEffect(() => cleanup, [cleanup]);

	return {
		drag,
		dropLine,
		preview,
		freeMode,
		ghostRef,
		handleItemMouseDown,
	};
};
